import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Loader2, Heart } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { createCheckout } from "../lib/api";

const TAX_RATE = 0.13; // Ontario HST

const CartPage = () => {
  const { items, updateQty, removeFromCart, subtotal } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [donation, setDonation] = useState(0);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const hasService = items.some((i) => i.type === "service");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please add your name and email", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id || i.id,
          variant_index: i.variant_index != null ? i.variant_index : null,
          size: i.size || null,
          qty: i.qty,
        })),
        customer: form,
        origin_url: window.location.origin,
        donation: donation > 0 ? donation : 0,
      };
      const { checkout_url } = await createCheckout(payload);
      window.location.href = checkout_url;
    } catch (err) {
      setLoading(false);
      toast({
        title: "Checkout failed",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (items.length === 0) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 mt-16 min-h-[50vh] flex flex-col items-center justify-center text-center">
        <ShoppingBag size={48} className="text-[#0e2a1e]/30" />
        <h1 className="font-serif-brand text-3xl font-semibold text-[#0e2a1e] mt-6">Your bag is empty</h1>
        <p className="text-[#0e2a1e]/60 mt-2">Looks like you haven't added anything yet.</p>
        <Link to="/shop" className="mt-8 bg-[#a4d61e] text-[#0e2a1e] font-semibold px-8 py-3.5 rounded-full hover:bg-[#b6e63a] transition-colors">
          Continue shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-[1400px] mx-auto px-6 mt-10 fade-up">
      <h1 className="font-serif-brand text-4xl font-semibold text-[#0e2a1e] mb-8">Your bag</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.id} className="flex gap-4 bg-white rounded-xl p-4 border border-[#0e2a1e]/8">
              <img src={item.image} alt={item.name} className="w-24 h-24 rounded-lg object-cover" />
              <div className="flex-1">
                <div className="flex justify-between">
                  <Link to={`/product/${item.product_id || item.id}`} className="font-medium text-[#0e2a1e] hover:text-[#a4820e]">
                    {item.name}
                  </Link>
                  <button onClick={() => removeFromCart(item.id)} className="text-[#0e2a1e]/40 hover:text-red-500 transition-colors">
                    <Trash2 size={18} />
                  </button>
                </div>
                {item.type === "service" ? (
                  <p className="text-xs text-[#2f6b1e] mt-1">${item.price.toFixed(2)} deposit now · balance on shoot day</p>
                ) : (
                  <p className="text-sm text-[#0e2a1e]/60 mt-1">${item.price.toFixed(2)}</p>
                )}
                <div className="flex items-center border border-[#0e2a1e]/20 rounded-full w-fit mt-3">
                  <button onClick={() => updateQty(item.id, item.qty - 1)} className="p-2 text-[#0e2a1e] hover:text-[#a4820e]">
                    <Minus size={14} />
                  </button>
                  <span className="w-8 text-center text-sm text-[#0e2a1e]">{item.qty}</span>
                  <button onClick={() => updateQty(item.id, item.qty + 1)} className="p-2 text-[#0e2a1e] hover:text-[#a4820e]">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
              <div className="font-medium text-[#0e2a1e] self-end">${(item.price * item.qty).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="bg-[#0e2a1e] text-[#f4f2ea] rounded-2xl p-6 h-fit">
          <h2 className="font-serif-brand text-xl font-semibold mb-5">Checkout</h2>
          <form onSubmit={handleCheckout} className="space-y-3">
            <input name="name" value={form.name} onChange={handleChange} placeholder="Full name*" className="w-full bg-[#f4f2ea] text-[#0e2a1e] rounded-md px-4 py-2.5 text-sm outline-none" />
            <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email*" className="w-full bg-[#f4f2ea] text-[#0e2a1e] rounded-md px-4 py-2.5 text-sm outline-none" />
            <input name="phone" value={form.phone} onChange={handleChange} placeholder="Phone (optional)" className="w-full bg-[#f4f2ea] text-[#0e2a1e] rounded-md px-4 py-2.5 text-sm outline-none" />
            <textarea name="note" value={form.note} onChange={handleChange} placeholder="Note (e.g. preferred dates)" rows={2} className="w-full bg-[#f4f2ea] text-[#0e2a1e] rounded-md px-4 py-2.5 text-sm outline-none resize-none" />

            {/* Donation add-on */}
            <div className="bg-[#f4f2ea]/10 rounded-xl p-3 border border-[#f4f2ea]/15">
              <div className="flex items-center gap-2">
                <Heart size={16} className="text-[#a4d61e]" />
                <span className="text-sm font-medium">Add a donation</span>
              </div>
              <p className="text-xs text-[#f4f2ea]/60 mt-1">Support Everyone's Budget &amp; our community.</p>
              <div className="flex items-center gap-2 mt-3">
                {[2, 5, 10].map((v) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setDonation((d) => (d === v ? 0 : v))}
                    className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${donation === v ? "bg-[#a4d61e] text-[#0e2a1e]" : "bg-[#f4f2ea]/10 text-[#f4f2ea] hover:bg-[#f4f2ea]/20"}`}
                  >
                    +${v}
                  </button>
                ))}
              </div>
              {donation > 0 && (
                <button type="button" onClick={() => setDonation(0)} className="text-[11px] text-[#f4f2ea]/60 mt-2 hover:underline">Remove donation</button>
              )}
            </div>

            <div className="border-t border-[#f4f2ea]/15 my-2" />
            <div className="flex justify-between text-sm text-[#f4f2ea]/80">
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-[#f4f2ea]/80">
              <span>HST (13%)</span>
              <span>${(subtotal * TAX_RATE).toFixed(2)}</span>
            </div>
            {donation > 0 && (
              <div className="flex justify-between text-sm text-[#a4d61e]">
                <span>Donation</span>
                <span>+${donation.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-lg pt-1">
              <span>Pay now</span>
              <span>${(subtotal + subtotal * TAX_RATE + donation).toFixed(2)}</span>
            </div>
            {hasService && (
              <p className="text-xs text-[#f4f2ea]/60">Photography totals are the non-refundable deposit; the balance is paid on the day of your shoot.</p>
            )}
            <button type="submit" disabled={loading} className="w-full bg-[#a4d61e] text-[#0e2a1e] font-semibold py-3.5 rounded-full hover:bg-[#b6e63a] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? (<><Loader2 size={18} className="animate-spin" /> Redirecting…</>) : "Pay with card"}
            </button>
            <p className="text-xs text-[#f4f2ea]/50 text-center">
              Secured by Stripe. CAD. All sales final. · <Link to="/donate" className="underline hover:text-[#a4d61e]">Donate separately</Link>
            </p>
          </form>
        </div>
      </div>
    </main>
  );
};

export default CartPage;
